import { useState, useEffect, useCallback } from "react";
import {
    Grid,
    Stack,
    Alert,
    Button,
    FormControl,
    FormLabel,
    Container,
    Typography,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FormInput, FormDatePicker, Splashscreen } from "src/components";
import PhoneReminderDialog from "./components/PhoneReminderDialog";
import {
    AccountCircleOutlined,
    DepartureBoardOutlined,
    DirectionsBusOutlined,
} from "@mui/icons-material";
import { Formik, Form, FormikHelpers } from "formik";
import {
    focusElement,
    initTripRequest,
    formatParaplanTrip,
    formatNavigatorTrip,
    OUT_OF_ZONE_ERROR,
    SAME_ADDRESS_ERROR,
    validateByCalendar,
    ReturnTripTypes,
    formatNavigatorTripTimes,
    initTripTimes,
} from "./helpers";

import { TripRequestSchema } from "./schema";
import { useAppDispatch, useAppSelector, useSelectedClient } from "src/hooks";
import { navigatorSelector, authAPI } from "src/modules/auth/redux/authSlice";
import { enqueueSnackbar } from "notistack";
import { tripsAPI } from "../../redux/tripSlice";
import { useTitle, useLocalStorage } from "react-use";
import { alertIfMissingFields, isErrorWithMessage } from "src/utils/helpers";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { TripRequestFormValues } from "./types";
import {
    GetDriverAndVehicleDetailsResponse,
    OnDemandTripRequest,
    StatusTypes,
} from "../../types";
import { PassioService } from "src/types";
import { isEqual } from "lodash";
import { tinykeys } from "tinykeys";
import { getProperty } from "dot-prop";
import LocationSelector from "./components/LocationSelector";
import MoreInfoSelector from "./components/MoreInfoSelector";
import ShortcutsDialog from "src/components/ShortcutsDialog";
import RatingsDialog from "../components/RatingsDialog";
import TimeSelector from "./components/TimeSelector";
import moment from "moment";

const PHONE_REMINDER_KEY = "phone-reminder-dismissed-at";
const REMINDER_INTERVAL = 24 * 60 * 60 * 1000;

const TripRequest = () => {
    useTitle("Request a ride");
    const { id = "" } = useParams(); // managers can request rides for clients
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const [showInfo, setShowInfo] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [initValues, setInitValues] = useState<TripRequestFormValues | any>(
        {}
    );

    const [processing, setProcessing] = useState({
        active: true,
        message: "Initializing Trip",
    });

    const [rating, setRating] = useState({
        id: "",
        open: false,
        driver: "",
    });

    const [phoneReminderDismissedAt, setPhoneReminderDismissedAt] =
        useLocalStorage<number>(PHONE_REMINDER_KEY, 0);
    const [showPhoneReminder, setShowPhoneReminder] = useState(false);

    const [tripId, setTripId] = useState("");

    const [requestTrip, { isLoading }] = tripsAPI.useRequestTripMutation();
    const [tdbHandler, { isLoading: isRequesting }] = tripsAPI.useTdbMutation();
    const [updateProfile] = authAPI.useUpdateProfileMutation();

    const theme = useTheme();
    const isMobileDevice = useMediaQuery(theme.breakpoints.down("md"));
    const client = useSelectedClient(id);
    const isNavigator = useAppSelector(navigatorSelector);

    // todo: canAutoschedule and canAutoApproveRequests are now handled by the tdb hook, consider removing it here
    const {
        user,
        archived,
        programs,
        isManager,
        availableFundingSources,
        negotiationRange,
        canAutoApproveRequests,
        canAutoScheduleTrips,
        canRequestAnyPULocation,
    } = useAppSelector((state) => ({
        archived: state.auth.client.archive,
        user: state.auth.user,
        programs: state.auth.programs,
        isManager: state.auth.isManager,
        availableFundingSources: state.auth.availableFundingSources,
        negotiationRange:
            state.auth.config?.ConnectPreferences?.NegotiationRange,
        canAutoScheduleTrips:
            state.auth.service === PassioService.navigator &&
            state.auth.config?.ConnectPreferences?.AutoScheduleHierarchy
                ?.SystemShouldAutoSchedule,
        canRequestAnyPULocation:
            state.auth.config?.ConnectPreferences?.CanRequestAnyPULocation,
        canAutoApproveRequests:
            state.auth.service === PassioService.navigator &&
            state.auth.config?.ConnectPreferences?.AutoApproveRequests,
    }));

    const [isOnDemand, setIsOnDemand] = useState(isNavigator && isMobileDevice);

    const [validateServiceCalendar, { isLoading: validatingCalendar }] =
        tripsAPI.useValidateServiceCalendarMutation();

    const loading = isLoading || isRequesting || validatingCalendar;

    // initialize trip
    useEffect(() => {
        (async () => {
            let values = await initTripRequest(
                client,
                user?.Email,
                canRequestAnyPULocation,
                isNavigator
            );
            // default to first available funding source if trip program is not set
            if (!values.tripProgram && !isNavigator) {
                if (availableFundingSources?.length) {
                    values.tripProgram = availableFundingSources[0];
                }
            }

            // if a trip duplication was requested
            if (location.state?.duplicate) {
                setInitValues({ ...values, ...location.state.duplicate });
            } else {
                setInitValues(values);
            }
            setProcessing({ active: false, message: "" });
        })();
    }, [
        client,
        programs,
        user?.Email,
        isNavigator,
        location.state,
        availableFundingSources,
        canRequestAnyPULocation,
    ]);

    // register keyboard shortcuts
    useEffect(() => {
        let unsubscribe = tinykeys(window, {
            // focus pick up address
            "$mod+Alt+P": (evt) => {
                evt.preventDefault();
                focusElement("pickUpPlace");
            },
            // focus drop off address
            "$mod+Alt+O": (evt) => {
                evt.preventDefault();
                focusElement("dropOffPlace");
            },
            // focus time type
            "$mod+Alt+I": (evt) => {
                evt.preventDefault();
                focusElement("timeType");
            },
            // show shortcuts dialog
            "$mod+Alt+H": (evt) => {
                evt.preventDefault();
                setShowShortcuts(true);
            },
        });
        return () => {
            unsubscribe();
        };
    }, []);

    function handleNavigate(tripId = "") {
        // if manager requesting trip go back to riders, reset state incase a trip was duplicated

        if (isNavigator) {
            return navigate(`/trips/${tripId}/transit`, { state: {} });
        }

        if (id && isManager) {
            return navigate(`/trips/${tripId}?status=live&rider=${id}`, {
                state: {},
            });
        }
        navigate(`/trips/${tripId}?status=live`, { state: {} });
    }

    const fetchDriverAndVehicleDetails = useCallback(
        async (runId = "") => {
            let response: GetDriverAndVehicleDetailsResponse = {
                driverName: "",
                vehicle: "",
            };

            if (!runId) return response;

            const { data: run } = await dispatch(
                tripsAPI.endpoints.getRun.initiate(runId)
            );
            if (!run || !run.driverId || !run.busId) return response;
            const { data: driverDetails } = await dispatch(
                tripsAPI.endpoints.getDriverAndVehicleDetails.initiate({
                    busId: run.busId,
                    driverId: run.driverId,
                })
            );
            if (driverDetails) response = driverDetails;
            return response;
        },
        [dispatch]
    );

    // todo: discuss when this is checked to after trip completion
    const getLastUnratedTrip = useCallback(async () => {
        try {
            const { data = [] } = await dispatch(
                tripsAPI.endpoints.getTrips.initiate(client.riderId)
            );

            // get the last unrated completed trip
            const trip = data.find(
                (trip) =>
                    trip.tripStatus === StatusTypes.Completed &&
                    trip.tripRating === 0
            );

            if (trip && trip.runId) {
                const details = await fetchDriverAndVehicleDetails(
                    String(trip.runId)
                );
                return {
                    id: trip.databaseId,
                    driver: details.driverName,
                };
            }
        } catch {}
    }, [client.riderId, dispatch, fetchDriverAndVehicleDetails]);

    async function handlePassioTripRequest(trip: OnDemandTripRequest) {
        const response = await tdbHandler({
            path: "add",
            body: {
                type: "onDemandTrip",
                userId: client.agencyId,
                riderId: client.riderId,
                ...trip,
            },
        }).unwrap();
        return response.id;
    }

    async function handleCalendarAndMembershipValidation(
        values: TripRequestFormValues
    ) {
        let errors = { timeError: "", membershipError: "", expired: false };
        try {
            if (!isNavigator) {
                const validation = await validateByCalendar(
                    client,
                    values.apptTime,
                    values.tripProgram
                );
                errors.timeError = validation.message;
                errors.expired = validation.expired;
            }

            // services and client profile fetched by TripPrograms component is automatically stored in state
            const service = programs.find(
                (item) => item.programName === values.tripProgram
            );

            if (!service) {
                errors.membershipError =
                    "Sorry the service you selected is unavailable";
                return errors;
            }

            if (service.membershipIsRequired) {
                const clientService = client?.programs?.find(
                    (item) => item.databaseID === service.databaseID
                );

                // membershipId gets added to client service when its parsed from riderProfile
                if (!clientService || !clientService?.membershipID)
                    errors.membershipError = `Sorry you are not preapproved to ride with ${values.tripProgram}. Please contact support`;
            }

            const isValid = await validateServiceCalendar({
                id: service.databaseID,
                date: moment(values.apptTime).format("YYYY-MM-DDTHH:mm:ss"),
            }).unwrap();

            if (!isValid)
                errors.timeError = `${values.tripProgram} is not available at this time`;
            // todo: get exact time service is valid for when onDemandAccessToken can call serviceCalendar
        } catch (error) {}

        // never let this fail
        return errors;
    }

    async function submitRequest(
        values: TripRequestFormValues,
        helpers: FormikHelpers<TripRequestFormValues>
    ): Promise<string | undefined> {
        try {
            // ensure Pickup and dropOff locations are different
            if (
                isEqual(
                    values.pickUpPlace.description,
                    values.dropOffPlace.description
                )
            ) {
                helpers.setFieldError(
                    "dropOffPlace.description",
                    SAME_ADDRESS_ERROR
                );
                enqueueSnackbar(SAME_ADDRESS_ERROR, { variant: "error" });
                return undefined;
            }

            // revalidate trip times and membership, time may expire while hidden behind the now button
            const { timeError, membershipError, expired } =
                await handleCalendarAndMembershipValidation(values);

            // If time is in the past and config is set to disable future requests, set the time to now
            // this check should be either or because even though the time is expired the time error will also be present
            if (expired) {
                // Paraplan, we use negotiation range for this check
                if (negotiationRange === 999) {
                    values = { ...values, ...initTripTimes() };
                }
            } else if (timeError) {
                helpers.setFieldError("apptTime", timeError);
                enqueueSnackbar(timeError, { variant: "error" });
                return undefined;
            }

            if (membershipError) {
                helpers.setFieldError("tripProgram", membershipError);
                enqueueSnackbar(membershipError, { variant: "error" });
                return undefined;
            }

            /**
             * Navigator trip request fields
             * https://github.com/Passio/passio/blob/master/tdb/type/user/goRideRequest/index.json
             */

            let tripId = "";
            const message =
                "Trip Requested successfully. A confirmation email will be sent shortly";

            if (isNavigator) {
                const service = programs.find(
                    (program) => program.programName === values.tripProgram
                );

                if (!service) {
                    throw new Error("Invalid service selected");
                }
                // reset time if service does not support future booking
                if (!service.riderCanScheduleFutureTrip) {
                    Object.assign(values, {
                        apptTime: moment()
                            .add(service.onDemandOffset, "m")
                            .toDate(),
                    });
                }

                let trip = await formatNavigatorTrip(
                    values,
                    client,
                    service,
                    canAutoScheduleTrips,
                    canAutoApproveRequests
                );

                //  request return trip first then add its id to the request
                if (values.returnType === ReturnTripTypes.specificTime) {
                    const returnTripValues = {
                        ...values,
                        apptTime: values.returnTime,
                    };

                    const times = await formatNavigatorTripTimes(
                        returnTripValues,
                        client,
                        service
                    );

                    const returnTrip = {
                        ...trip,
                        ...times,
                        pickUpPlace: trip.dropOffPlace,
                        dropOffPlace: trip.pickUpPlace,
                    };

                    trip.nextOnDemandTripId = await handlePassioTripRequest(
                        returnTrip
                    );
                }

                tripId = await handlePassioTripRequest(trip);

                // check if last successful trip was rated
                const lastUnrated = await getLastUnratedTrip();
                if (lastUnrated) {
                    // stash trip id to navigate to trip details incase ratings need to be done
                    setTripId(tripId);
                    setRating({ ...lastUnrated, open: true });
                    enqueueSnackbar(message, { variant: "success" });
                    return tripId;
                }
            } else {
                const trip = formatParaplanTrip(values, user);
                const response = await requestTrip(trip).unwrap();
                tripId = response.AddedTrips[0];
            }
            enqueueSnackbar(message, { variant: "success" });
            return tripId;
        } catch (error) {
            const message = isErrorWithMessage(error)
                ? error.message
                : "Trip request was unsuccessfull, please try again";
            enqueueSnackbar(message, { variant: "error" });
            return undefined;
        }
    }

    async function handleRequest(
        values: TripRequestFormValues,
        helpers: FormikHelpers<TripRequestFormValues>
    ) {
        const id = await submitRequest(values, helpers);
        if (!id) return;
        setTripId(id);
        if (!client.phone) {
            const last = phoneReminderDismissedAt || 0;
            if (Date.now() - last > REMINDER_INTERVAL) {
                setShowPhoneReminder(true);
                return;
            }
        }
        handleNavigate(id);
    }

    // initializing trip could take a while
    if (processing.active) {
        return <Splashscreen description={processing.message} />;
    }

    return (
        <Container maxWidth="xl" sx={{ p: { md: 2 } }}>
            {Boolean(archived) && (
                <Alert severity="error" sx={{ my: 2 }}>
                    Your account has been archived and cannot request new trips.
                    Please contact support
                </Alert>
            )}
            <Formik
                initialValues={initValues}
                validationSchema={TripRequestSchema}
                onSubmit={handleRequest}
            >
                {({ values, errors, isValidating, isSubmitting }) => {
                    /**
                     *  Effectively disallow form submission if addresses are similar or out of zone
                     *  without showing a frustrating disabled button
                     */
                    const pickupAddressError = getProperty(
                        errors.pickUpPlace,
                        "description"
                    );
                    const dropOffAddressError = getProperty(
                        errors.dropOffPlace,
                        "description"
                    );
                    const shouldNotSubmit =
                        isValidating ||
                        pickupAddressError === OUT_OF_ZONE_ERROR ||
                        dropOffAddressError === OUT_OF_ZONE_ERROR ||
                        dropOffAddressError === SAME_ADDRESS_ERROR ||
                        errors.apptTime || // trip time errors
                        errors.tripProgram; // tripProgram error

                    return (
                        <Form>
                            {/* Header */}
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent={{
                                    xs: "center",
                                    md: "space-between",
                                }}
                                my={{ xs: 2, md: 4 }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <DirectionsBusOutlined fontSize="large" />
                                    <Typography variant="h4" component="h1">
                                        Request A Ride
                                    </Typography>
                                </Stack>

                                {id && isManager && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <AccountCircleOutlined fontSize="large" />
                                        <Typography
                                            variant="h5"
                                            component="span"
                                        >
                                            {values.clientFirstName}{" "}
                                            {values.clientLastName}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            sx={{ ml: 2 }}
                                            onClick={() =>
                                                setShowInfo(!showInfo)
                                            }
                                        >
                                            {showInfo ? "Hide" : "View"}
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>

                            <Grid container>
                                {/* Rider details only visible to managers */}
                                {isManager && showInfo && (
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            p: 2,
                                            mb: 4,
                                            boxShadow: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <FormControl
                                            component="fieldset"
                                            sx={{
                                                mb: 4,
                                                display: "flex",
                                                gap: 2,
                                            }}
                                        >
                                            <FormLabel
                                                component="legend"
                                                sx={{
                                                    mb: 2,
                                                    fontSize: 20,
                                                    color: "primary.main",
                                                }}
                                            >
                                                Rider
                                            </FormLabel>

                                            {/* Rider Information */}
                                            <Stack
                                                spacing={2}
                                                direction={{
                                                    xs: "column",
                                                    md: "row",
                                                }}
                                            >
                                                <FormInput
                                                    name="clientFirstName"
                                                    label="First Name"
                                                    sx={{ flexGrow: 1 }}
                                                />
                                                <FormInput
                                                    name="clientLastName"
                                                    label="Last Name"
                                                    sx={{ flexGrow: 1 }}
                                                />
                                            </Stack>

                                            <Stack
                                                spacing={2}
                                                direction={{
                                                    xs: "column",
                                                    md: "row",
                                                }}
                                            >
                                                <FormInput
                                                    type="tel"
                                                    required
                                                    name="clientPhone"
                                                    label="Phone"
                                                    sx={{ flexGrow: 1 }}
                                                    autoComplete="tel"
                                                />

                                                <FormDatePicker
                                                    format="MM/dd/yyyy"
                                                    name="clientBirthdate"
                                                    label="Birthday"
                                                    sx={{ flexGrow: 1 }}
                                                />

                                                <FormInput
                                                    type="email"
                                                    name="clientEmail"
                                                    label="Rider Email"
                                                    sx={{ flexGrow: 1 }}
                                                />
                                            </Stack>

                                            {/* requester details */}
                                            <Stack spacing={2}>
                                                <FormInput
                                                    type="email"
                                                    name="requesterEmail"
                                                    label="Requester Email"
                                                    sx={{ flexGrow: 1 }}
                                                />

                                                <FormInput
                                                    multiline
                                                    minRows={5}
                                                    fullWidth
                                                    name="clientComments"
                                                    label="Comments"
                                                />
                                            </Stack>
                                        </FormControl>
                                    </Grid>
                                )}

                                {/* Location Selector  */}
                                <LocationSelector isOnDemand={isOnDemand} />

                                {/* times */}
                                {!isOnDemand && (
                                    <TimeSelector client={client} />
                                )}

                                {/* more information */}
                                <MoreInfoSelector />

                                <Grid item xs={12} my={4} mx="auto">
                                    <LoadingButton
                                        fullWidth
                                        type={
                                            // we could just disable but that tends to be frustrating
                                            shouldNotSubmit
                                                ? "button"
                                                : "submit"
                                        }
                                        variant="contained"
                                        sx={{ py: 1.5 }}
                                        loading={
                                            loading ||
                                            isValidating ||
                                            isSubmitting
                                        }
                                        disabled={Boolean(archived)}
                                        onClick={() =>
                                            alertIfMissingFields(errors)
                                        }
                                    >
                                        Request trip
                                    </LoadingButton>

                                    {isOnDemand && (
                                        <Button
                                            fullWidth
                                            variant={"outlined"}
                                            startIcon={
                                                <DepartureBoardOutlined />
                                            }
                                            sx={{ py: 1.5, mt: 2 }}
                                            onClick={() => {
                                                setIsOnDemand(false);
                                                window.scrollTo({
                                                    top: 0,
                                                    behavior: "smooth",
                                                });
                                            }}
                                        >
                                            Schedule A Ride
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
            <ShortcutsDialog
                open={showShortcuts}
                onClose={() => setShowShortcuts(false)}
            />

            {isNavigator && (
                <RatingsDialog
                    id={rating.id}
                    open={rating.open}
                    rating={0}
                    onClose={() => handleNavigate(tripId)}
                    description={`We take your feedback very seriously. Please rate your previous trip with ${rating.driver}`}
                />
            )}

            <PhoneReminderDialog
                open={showPhoneReminder}
                onSave={async (phone) => {
                    try {
                        await updateProfile({
                            onDemandUser: { id: String(client.id), phone },
                            rider: { id: String(client.riderId) },
                            paraplan: { id: client.id, phone },
                        }).unwrap();
                        enqueueSnackbar("Phone number updated", {
                            variant: "success",
                        });
                    } catch (error) {
                        enqueueSnackbar("Sorry, an error occured", {
                            variant: "error",
                        });
                    } finally {
                        setShowPhoneReminder(false);
                        if (tripId) {
                            handleNavigate(tripId);
                        }
                    }
                }}
                onRemindLater={() => {
                    setPhoneReminderDismissedAt(Date.now());
                    setShowPhoneReminder(false);
                    if (tripId) {
                        handleNavigate(tripId);
                    }
                }}
            />
        </Container>
    );
};

export default TripRequest;
